import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { Trans } from '@lingui/react/macro';
import { useAtomValue } from 'jotai';
import { useParams } from 'react-router-dom';

import { useGetZuubInsuranceDetail } from '@/react-query/useGetZuubInsuranceDetail';
import { useScrollSpy } from '@/utils/hooks/useScrollSpy';

import { selectedPlanAtom } from '../atoms';
import { BenefitsPageTopbar } from '../BenefitsPageTopbar';

import { BenefitsPageSidebar } from './BenefitsPageSidebar';

interface PatientBenefitsLayoutProps {
  children: ReactNode;
}

export const PatientBenefitsLayout = ({
  children,
}: PatientBenefitsLayoutProps) => {
  const { patientId } = useParams<{ patientId: string }>();
  const currentPlan = useAtomValue(selectedPlanAtom);

  // Check loading state for the benefits data
  const { isLoading, data: insuranceData } = useGetZuubInsuranceDetail({
    pmsPatientId: patientId,
    enabled: !!patientId,
  });

  // Generate coverage details subsections from the selected plan
  const coverageDetailsSubsections = useMemo(() => {
    if (!currentPlan?.coverage) {
      return [];
    }

    return currentPlan.coverage.map(
      (coverage) =>
        coverage.categoryName
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-]/g, '') + '-details',
    );
  }, [currentPlan]);

  const sectionIds = {
    // Standalone sections (no subsections)
    'plan-summary-section': [],
    'patient-information-section': [],
    'coverage-summary-section': [],
    'maximums-deductibles-section': [],
    'limitations-and-disclaimers-section': [],
    'waiting-periods-section': [],
    'treatment-history-section': [],
    // Sections with subsections
    'coverage-details': coverageDetailsSubsections,
  };

  // Use scroll spy to track which section is currently in view
  const scrollSpyResult = useScrollSpy({
    sectionIds,
    rootMargin: '0px 0px -50% 0px', // Only consider sections that are at least half visible
    hasManualSelection: true,
  });

  // Type guard to ensure we have the object return type
  const activeSection =
    typeof scrollSpyResult === 'object'
      ? scrollSpyResult.activeSection
      : scrollSpyResult;

  const setActiveSection =
    typeof scrollSpyResult === 'object'
      ? scrollSpyResult.setActiveSection
      : () => {};

  const handleTitleClick =
    typeof scrollSpyResult === 'object'
      ? scrollSpyResult.handleTitleClick
      : undefined;

  return (
    <div className="vai-grid vai-grid-rows-[124px_1fr] vai-h-screen vai-w-screen vai-overflow-hidden">
      {/* Topbar - Full Width */}
      <BenefitsPageTopbar />

      {/* Main Content - Constrained Width */}
      <div className="vai-flex vai-bg-gray-50 vai-overflow-hidden">
        {/* Sidebar */}
        <div className="vai-flex-shrink-0">
          <BenefitsPageSidebar
            activeSection={activeSection}
            onSectionClick={setActiveSection}
            onTitleClick={handleTitleClick}
          />
        </div>

        {/* Main Content Area */}
        <div
          id="benefits-content-area"
          className="vai-flex-1 vai-p-6 vai-overflow-y-auto"
          style={{ scrollBehavior: 'smooth' }}
        >
          <div className="vai-w-full vai-mx-auto vai-h-full">
            {isLoading ? (
              <div className="vai-flex vai-justify-center vai-items-center vai-h-full">
                <div className="vai-border-4 vai-border-gray-200 vai-border-t-blue-500 vai-rounded-full vai-w-10 vai-h-10 vai-animate-spin" />
              </div>
            ) : !insuranceData?.found ? (
              <div className="vai-flex vai-flex-col vai-items-center vai-justify-center vai-h-[60vh] vai-gap-4">
                <p className="vai-text-gray-600 vai-text-lg">
                  <Trans>No benefits data found for this patient.</Trans>
                </p>
              </div>
            ) : (
              children
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
